/**
 * 取引先(共通マスタ)の区分と支払条件(2026-10-23 決定: 支払期日は設定式)。
 *
 * docs/specs/2026-10-全体設計.md 3章・4章:
 *  - 取引先ごとに「締め日・支払月・支払日・土日祝の扱い」を持ち、支払期日(入金予定日)を自動で出す
 *  - 支払キューの1件ずつ、経理が期日を直せる(この関数は既定の期日を出すだけ)
 *  - 土日祝のずらしは繰り返し支払いと同じ shiftForHoliday()(祝日データ jpHolidays.ts)
 *
 * 具体例は tests/unit/partnerTerms.test.ts
 */
import { endOfMonth, toMonth } from '@core/lib/date';
import { addMonths, shiftForHoliday } from '@core/recurring/schedule';
import type { DateStr, DayOrEnd, HolidayShift, MonthStr, PartnerKind, PaymentTerms, Vendor } from '@core/lib/types';

export const PARTNER_KIND_OPTIONS: Array<{ value: PartnerKind; label: string }> = [
  { value: 'prime', label: '元請け' },
  { value: 'customer', label: '売上先' },
  { value: 'subcontractor', label: '下請け' },
  { value: 'supplier', label: '仕入先' },
];

export function partnerKindLabel(kind: PartnerKind): string {
  return PARTNER_KIND_OPTIONS.find((o) => o.value === kind)?.label ?? kind;
}

/** 区分。旧データ(未設定)は支払い先マスタとして登録されたものなので「仕入先」(経費の支払先を含む) */
export function kindsOf(v: Pick<Vendor, 'kinds'>): PartnerKind[] {
  return v.kinds && v.kinds.length > 0 ? v.kinds : ['supplier'];
}

/** 当社が受け取る側(請求書を出す相手)か */
export function isReceivable(v: Pick<Vendor, 'kinds'>): boolean {
  return kindsOf(v).some((k) => k === 'prime' || k === 'customer');
}

/** 当社が支払う側か */
export function isPayable(v: Pick<Vendor, 'kinds'>): boolean {
  return kindsOf(v).some((k) => k === 'subcontractor' || k === 'supplier');
}

export const MONTH_OFFSET_OPTIONS: Array<{ value: PaymentTerms['month_offset']; label: string }> = [
  { value: 0, label: '当月' },
  { value: 1, label: '翌月' },
  { value: 2, label: '翌々月' },
  { value: 3, label: '3か月後' },
];

export const HOLIDAY_SHIFT_OPTIONS: Array<{ value: HolidayShift; label: string }> = [
  { value: 'before', label: '前の平日(前倒し)' },
  { value: 'after', label: '後の平日(後ろ倒し)' },
  { value: 'none', label: 'ずらさない' },
];

function dayLabel(day: DayOrEnd): string {
  return day === 'end' ? '末' : `${day}`;
}

/** 一覧用の短い表記。例: 「末締め 翌々月5日」(土日祝の扱いは termsLabel で) */
export function termsShortLabel(t: PaymentTerms | null | undefined): string {
  if (!t) return '未設定';
  const month = MONTH_OFFSET_OPTIONS.find((o) => o.value === t.month_offset)?.label ?? `${t.month_offset}か月後`;
  return `${dayLabel(t.closing_day)}締め ${month}${t.pay_day === 'end' ? '末日' : `${t.pay_day}日`}`;
}

/** 例: 「末締め 翌々月5日(土日祝は後ろ倒し)」 */
export function termsLabel(t: PaymentTerms | null | undefined): string {
  if (!t) return '未設定';
  const month = MONTH_OFFSET_OPTIONS.find((o) => o.value === t.month_offset)?.label ?? `${t.month_offset}か月後`;
  const pay = t.pay_day === 'end' ? '末日' : `${t.pay_day}日`;
  const shift =
    t.holiday_shift === 'before' ? '(土日祝は前倒し)' : t.holiday_shift === 'after' ? '(土日祝は後ろ倒し)' : '';
  return `${dayLabel(t.closing_day)}締め ${month}${pay}${shift}`;
}

function isDay(v: unknown): v is DayOrEnd {
  return v === 'end' || (typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 31);
}

/** 入力の誤り。問題なければ null */
export function validateTerms(t: PaymentTerms): string | null {
  if (!isDay(t.closing_day)) return '締め日は1〜31日または末日を選んでください';
  if (!isDay(t.pay_day)) return '支払日は1〜31日または末日を選んでください';
  if (![0, 1, 2, 3].includes(t.month_offset)) return '支払月を選んでください';
  if (!['before', 'after', 'none'].includes(t.holiday_shift)) return '土日祝の扱いを選んでください';
  if (t.month_offset === 0) {
    // 当月払いは、支払日が締め日より後でないと締める前に払うことになる
    const close = t.closing_day === 'end' ? 31 : t.closing_day;
    const pay = t.pay_day === 'end' ? 31 : t.pay_day;
    if (pay < close) {
      return '当月払いの場合、支払日は締め日より後にしてください';
    }
  }
  return null;
}

/** その月の指定日。存在しない日(例: 2月31日)は末日に丸める */
export function dayOfMonth(month: MonthStr, day: DayOrEnd): DateStr {
  if (day === 'end') return endOfMonth(month);
  const last = Number(endOfMonth(month).slice(8, 10));
  return `${month}-${String(Math.min(Math.max(day, 1), last)).padStart(2, '0')}`;
}

/** base(計上日・請求日など)が属する締めの日。締め日を過ぎていれば翌月の締め日 */
export function closingDateFor(base: DateStr, closingDay: DayOrEnd): DateStr {
  const month = toMonth(base);
  const thisClose = dayOfMonth(month, closingDay);
  return base <= thisClose ? thisClose : dayOfMonth(addMonths(month, 1), closingDay);
}

export type DueDate = {
  /** 締めの日 */
  closingDate: DateStr;
  /** 条件どおりの支払日(土日祝をずらす前) */
  scheduledDate: DateStr;
  /** 土日祝をずらした支払期日(CF の予定日) */
  dueDate: DateStr;
};

/** 支払期日(入金予定日)。例: 末締め翌々月5日・後ろ倒しで base=2026-10-15 → 締め10/31 → 12/5(土)→ 12/7(月) */
export function dueDateFor(base: DateStr, terms: PaymentTerms): DueDate {
  const closingDate = closingDateFor(base, terms.closing_day);
  const scheduledDate = dayOfMonth(addMonths(toMonth(closingDate), terms.month_offset), terms.pay_day);
  return { closingDate, scheduledDate, dueDate: shiftForHoliday(scheduledDate, terms.holiday_shift) };
}

/** インボイス登録番号を T+13桁 にそろえる(全角・ハイフン・空白を許す)。形が違えば null */
export function normalizeInvoiceRegNo(raw: string): string | null {
  const s = raw.normalize('NFKC').replace(/[\s-]/g, '').toUpperCase();
  if (s === '') return null;
  const m = /^T?(\d{13})$/.exec(s);
  return m ? `T${m[1]}` : null;
}

/** 名称の重複判定用(全角/半角・空白の違いを無視する) */
export function nameKey(name: string): string {
  return name.normalize('NFKC').replace(/\s/g, '').toLowerCase();
}
